import type { DateTime } from 'luxon';
import { RRule } from 'rrule';
import { Frequency, type ParsedOptions } from 'rrule/dist/esm/types';
import { TIME_ZONE_UTC } from '@/constants';
import type { PageExperienceFragment } from '@/features/experiences/fragments/PageExperience.generated';
import {
  getAuthorTimezone,
  getDisplayTimezone,
} from '@/features/experiences/lib/experience';
import { datePartInZone } from '@/utils/dates';
import type { ExperienceEventFragment } from '../fragments/ExperienceEventFragment.generated';
import type { MagicEventFragment } from '../fragments/MagicEventFragment.generated';
import type {
  CollectionBase,
  DecoratedEvent,
  DecoratedExperienceEvent,
  ToolResultEvent,
} from '../types';
import { createDecoratedExperienceEvent } from './createDecoratedExperienceEvent';
import { createDecoratedMagicEvent } from './createDecoratedMagicEvent';

export const isExperienceEvent = (
  eventItem: DecoratedEvent
): eventItem is DecoratedExperienceEvent => {
  return 'origin' in eventItem || 'interactionApps' in eventItem;
};

export const hasOrigin = (
  eventItem: DecoratedEvent
): eventItem is DecoratedExperienceEvent => {
  return isExperienceEvent(eventItem) && Boolean(eventItem.origin);
};

type EventTiming = Pick<MagicEventFragment, 'isAllDay' | 'rrule' | 'start'>;

/**
 * Whether two versions of an event are scheduled identically — same repeat
 * rule, same start. Differing timing is what invalidates anything pinned to the
 * event's instances (its scheduled messages, its hidden dates).
 */
export const hasSameTiming = (
  first: EventTiming,
  second: EventTiming
): boolean =>
  first.rrule === second.rrule &&
  first.isAllDay === second.isAllDay &&
  first.start.date === second.start.date &&
  first.start.dateTime === second.start.dateTime &&
  first.start.timeZone === second.start.timeZone;

/**
 * The zone an event's wall clock should be read in, mirroring the rule the
 * server applies when it schedules text messages (services/eventMessage.ts).
 *
 * An all-day event's own `timeZone` is a hard-coded UTC sentinel rather than an
 * authored zone, so anything that has to resolve to a real time of day — "9am
 * the day of", the hour handed to an event gaining a time — has to fall back to
 * the experience's zone or it lands at that wall clock in UTC instead.
 */
export const getEventTimeZone = (
  event: Pick<DecoratedEvent, 'isAllDay' | 'timeZone'>,
  experience?: PageExperienceFragment
): string => {
  if (!event.isAllDay) {
    return event.timeZone;
  }

  return experience ? getAuthorTimezone(experience) : TIME_ZONE_UTC;
};

type CalcTimingOptions = {
  isAllDay: boolean;
};

export const calcTiming = (
  date: DateTime<true>,
  { isAllDay }: CalcTimingOptions
): DecoratedExperienceEvent['start'] => {
  const timing: DecoratedExperienceEvent['start'] = {};

  if (isAllDay) {
    timing.date = datePartInZone(date, TIME_ZONE_UTC).toISODate();
  } else {
    timing.dateTime = date.toUTC().toISO();
  }

  return timing;
};

export const createDecoratedEvent = (
  base: CollectionBase,
  rawEvent: ExperienceEventFragment | MagicEventFragment
): DecoratedEvent => {
  if (base.experience) {
    const isSubscribed =
      'isSubscribed' in rawEvent ? rawEvent.isSubscribed : false;
    const exportedToExperiences =
      'exportedToExperiences' in rawEvent ? rawEvent.exportedToExperiences : [];
    const metadata =
      'metadata' in rawEvent
        ? rawEvent.metadata
        : {
            category: null,
            hidden: [],
            inheritedHidden: [],
            labels: [],
            whoLabels: [],
          };
    return createDecoratedExperienceEvent(
      { ...rawEvent, isSubscribed, exportedToExperiences, metadata },
      base.experience,
      getDisplayTimezone(base.experience)
    );
  }

  return createDecoratedMagicEvent(rawEvent);
};

type FrequencyOption =
  'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | 'EVERY_OTHER_WEEK' | 'ONCE';

const WEEKDAY_OPTIONS = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
] as const;

const toFrequencyOption = (rrule: ParsedOptions): FrequencyOption => {
  if (rrule.freq === Frequency.DAILY) {
    return 'DAILY';
  }
  if (rrule.freq === Frequency.WEEKLY) {
    if (rrule.interval === 2) {
      return 'EVERY_OTHER_WEEK';
    }
    return 'WEEKLY';
  }
  if (rrule.freq === Frequency.MONTHLY) {
    return 'MONTHLY';
  }
  if (rrule.freq === Frequency.YEARLY) {
    return 'YEARLY';
  }
  return 'ONCE';
};

export const toToolResult = (item: DecoratedEvent): ToolResultEvent => {
  const { options: rrule } = item.rrule ? RRule.fromString(item.rrule) : {};

  return {
    byMonthDay: rrule?.bymonthday?.[0],
    bySetPos: rrule?.bysetpos?.[0],
    byWeekDay: rrule?.byweekday?.map((index) => WEEKDAY_OPTIONS[index])[0],
    description: item.description || undefined,
    emoji: item.emoji || undefined,
    endDate: item.endDate.toISODate(),
    endTime: item.isAllDay ? undefined : item.endDate.toISOTime(),
    eventId: item.id,
    frequency: rrule ? toFrequencyOption(rrule) : undefined,
    isAllDay: item.isAllDay,
    isRecurring: Boolean(item.rrule),
    location: item.locationWithPlace?.name || undefined,
    startDate: item.startDate.toISODate(),
    startTime: item.isAllDay ? undefined : item.startDate.toISOTime(),
    title: item.title,
  };
};
